/**
 * Sysadmin Platform → Builder Analytics (AUTO-6).
 *
 * v1 dashboard for builder telemetry. Six builder kinds, each with:
 *   - turn count
 *   - first-token p50 / p95
 *   - discovery contract pass-rate badge
 *   - tool call success rate
 *   - top called + top erroring tools (lazy-loaded per builder)
 *
 * Plus:
 *   - Cross-builder comparison table (sortable)
 *   - Per-tenant breakdown table (sortable by turn count desc default)
 *   - Publish-gate panel (entity kind, count, time-in-pending p95)
 *
 * Design conventions follow `platform.hardware-profiles.tsx` (same
 * author, same primitives — StatTile / SectionCard / PageError / Badge).
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  Bot,
  CircleAlert,
  Clock,
  Gauge,
  ListChecks,
  Sparkles,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SectionCard } from '@/components/ui/section-card';
import { PageError } from '@/components/ui/page-error';
import { StatTile, type StatTileTone } from '@/components/ui/stat-tile';
import {
  useBuilderAnalyticsSummary,
  useBuilderPerTenant,
  useBuilderTopTools,
  usePublishGateSummary,
  discoveryBadge,
  formatMs,
  formatPct,
  BUILDER_KIND_LABELS,
  BUILDER_KIND_ENUM,
  type BuilderKind,
  type DateRange,
  type BuilderSummaryCard,
  type DiscoveryBadge,
} from '@/lib/use-sysadmin-builder-analytics';

export const Route = createFileRoute('/sysadmin/platform/builder-analytics')({
  component: BuilderAnalyticsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────

const DISCOVERY_BADGE_TONE: Record<DiscoveryBadge, StatTileTone> = {
  green: 'success',
  yellow: 'warning',
  red: 'destructive',
  unknown: 'muted',
};

const DISCOVERY_BADGE_LABEL: Record<DiscoveryBadge, string> = {
  green: 'Discovery OK',
  yellow: 'Discovery degraded',
  red: 'Discovery FAILING',
  unknown: 'No T1 data',
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function nDaysAgoIso(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// ─── Top-level page ───────────────────────────────────────────────────────

function BuilderAnalyticsPage() {
  // Date range — defaults to last 7 days. ISO YYYY-MM-DD strings so we
  // can drop them into the URL params directly.
  const [from, setFrom] = useState<string>(nDaysAgoIso(7));
  const [to, setTo] = useState<string>(todayIso());

  const range: DateRange = useMemo(() => ({ from, to }), [from, to]);

  const summary = useBuilderAnalyticsSummary(range);
  const perTenant = useBuilderPerTenant(range);
  const publishGate = usePublishGateSummary(range);

  if (summary.error) {
    return (
      <PageError
        title="Failed to load builder analytics"
        message={summary.error.message}
        onRetry={summary.refetch}
      />
    );
  }

  const builders = summary.data?.builders ?? [];

  // Cross-cutting top-line numbers
  const totalTurns = builders.reduce((s, b) => s + b.turnCount, 0);
  const totalSuccess = builders.reduce((s, b) => s + b.successCount, 0);
  const totalTimeouts = builders.reduce(
    (s, b) => s + b.timeoutFirstTokenCount + b.timeoutStreamInactivityCount,
    0,
  );
  const overallSuccessRate = totalTurns > 0 ? totalSuccess / totalTurns : 0;

  const overallDiscoveryGreen = builders.filter(
    (b) => discoveryBadge(b) === 'green',
  ).length;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Activity className="size-6 text-primary" />
            Builder Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live signal from the six system builders (Ask Argus, Architect,
            and the four canvas helpers). Refreshes every 10 seconds.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="from">
              From
            </label>
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to}
              className="h-9 w-[150px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="to">
              To
            </label>
            <Input
              id="to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from}
              max={todayIso()}
              className="h-9 w-[150px]"
            />
          </div>
        </div>
      </div>

      {/* Top stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Total turns"
          value={totalTurns}
          icon={Sparkles}
        />
        <StatTile
          label="Success rate"
          value={formatPct(overallSuccessRate)}
          tone={overallSuccessRate >= 0.95 ? 'success' : overallSuccessRate >= 0.8 ? 'warning' : 'destructive'}
          icon={Gauge}
        />
        <StatTile
          label="Timeouts"
          value={totalTimeouts}
          tone={totalTimeouts === 0 ? 'success' : totalTimeouts < 5 ? 'warning' : 'destructive'}
          icon={Clock}
        />
        <StatTile
          label="Builders w/ discovery GREEN"
          value={overallDiscoveryGreen}
          suffix={`/ ${builders.length}`}
          tone={overallDiscoveryGreen === builders.length ? 'success' : 'warning'}
          icon={ListChecks}
        />
      </div>

      {/* Per-builder cards (6) */}
      <SectionCard
        title="Per-builder performance"
        icon={Bot}
        description="One card per system builder. Click a card to drill into its top tools."
      >
        {summary.isLoading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            Loading builder summaries…
          </div>
        ) : builders.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No telemetry recorded in this window yet. Trigger a few helper
            chats and the dashboard will populate within ~10s of the next
            daily rollup (manual rollup via POST /admin/platform/builder-
            analytics/rollup).
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {builders.map((b) => (
              <BuilderCard key={b.builderKind} card={b} range={range} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Cross-builder comparison table */}
      <SectionCard
        title="Cross-builder comparison"
        icon={Gauge}
        description="Side-by-side latency + reliability snapshot. Sorted by turn count desc."
      >
        <ComparisonTable builders={builders} />
      </SectionCard>

      {/* Per-tenant breakdown */}
      <SectionCard
        title="Per-tenant usage"
        icon={Activity}
        description="Top 100 tenants by turn count in this window."
      >
        {perTenant.error ? (
          <div className="text-sm text-destructive">
            Failed to load per-tenant breakdown: {perTenant.error.message}
          </div>
        ) : perTenant.isLoading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : (
          <PerTenantTable rows={perTenant.data?.rows ?? []} />
        )}
      </SectionCard>

      {/* Publish gate */}
      <SectionCard
        title="Publish-approval-gate activity"
        icon={ListChecks}
        description="Time-in-pending p95 per entity kind. Helps spot review backlogs."
      >
        {publishGate.error ? (
          <div className="text-sm text-destructive">
            Failed to load publish-gate summary: {publishGate.error.message}
          </div>
        ) : publishGate.isLoading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : (
          <PublishGateTable rows={publishGate.data?.rows ?? []} />
        )}
      </SectionCard>
    </div>
  );
}

// ─── Per-builder card ─────────────────────────────────────────────────────

function BuilderCard({
  card,
  range,
}: {
  card: BuilderSummaryCard;
  range: DateRange;
}) {
  const badge = discoveryBadge(card);
  const tone = DISCOVERY_BADGE_TONE[badge];
  const label = BUILDER_KIND_LABELS[card.builderKind];

  return (
    <div className="rounded-md border border-border bg-background p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-muted-foreground">
            {card.builderKind}
          </div>
        </div>
        <Badge
          variant={
            tone === 'success'
              ? 'default'
              : tone === 'destructive'
                ? 'destructive'
                : 'secondary'
          }
        >
          {DISCOVERY_BADGE_LABEL[badge]}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Turns</div>
          <div className="font-semibold tabular-nums">{card.turnCount}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Success</div>
          <div className="font-semibold tabular-nums">
            {formatPct(card.successRate)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">First-token p50</div>
          <div className="font-semibold tabular-nums">
            {formatMs(card.firstTokenP50Ms)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">First-token p95</div>
          <div className="font-semibold tabular-nums">
            {formatMs(card.firstTokenP95Ms)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Tool-call success</div>
          <div className="font-semibold tabular-nums">
            {card.toolCallAttemptCount > 0
              ? formatPct(card.toolCallSuccessRate)
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Discovery / Stub</div>
          <div className="font-semibold tabular-nums">
            {card.discoveryTurnCount} / {card.stubDraftCount}
          </div>
        </div>
      </div>

      {(card.timeoutFirstTokenCount > 0 || card.timeoutStreamInactivityCount > 0) && (
        <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="size-3.5" />
          {card.timeoutFirstTokenCount} first-token + {card.timeoutStreamInactivityCount} mid-stream timeouts
        </div>
      )}

      {/* Top tools (lazy) */}
      <TopToolsForBuilder builder={card.builderKind} range={range} />
    </div>
  );
}

function TopToolsForBuilder({
  builder,
  range,
}: {
  builder: BuilderKind;
  range: DateRange;
}) {
  const top = useBuilderTopTools({ builder, range, limit: 3 });
  const failing = useBuilderTopTools({
    builder,
    range,
    limit: 3,
    failuresFirst: true,
  });

  if (top.isLoading) {
    return <div className="text-xs text-muted-foreground">Loading tools…</div>;
  }
  if (top.error) {
    return (
      <div className="text-xs text-destructive">
        Tool stats unavailable: {top.error.message}
      </div>
    );
  }
  const topRows = top.data?.rows ?? [];
  const failingRows = (failing.data?.rows ?? []).filter((r) => r.failCount > 0);

  if (topRows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No tool calls in this window.
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-2 space-y-2">
      <div>
        <div className="text-xs font-medium text-muted-foreground mb-1">
          Top tools
        </div>
        <ul className="space-y-0.5">
          {topRows.map((r) => (
            <li key={r.toolSlug} className="flex justify-between text-xs">
              <span className="truncate" title={r.toolSlug}>{r.toolSlug}</span>
              <span className="tabular-nums text-muted-foreground">
                {r.attemptCount} · {formatPct(r.successRate)}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {failingRows.length > 0 && (
        <div>
          <div className="text-xs font-medium text-destructive mb-1 flex items-center gap-1">
            <CircleAlert className="size-3" /> Top failing
          </div>
          <ul className="space-y-0.5">
            {failingRows.map((r) => (
              <li
                key={`${r.toolSlug}-fail`}
                className="flex justify-between text-xs"
              >
                <span className="truncate" title={r.toolSlug}>
                  {r.toolSlug}
                </span>
                <span className="tabular-nums text-destructive">
                  {r.failCount} failed
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Comparison table ────────────────────────────────────────────────────

function ComparisonTable({ builders }: { builders: BuilderSummaryCard[] }) {
  const sorted = [...builders].sort((a, b) => b.turnCount - a.turnCount);
  if (sorted.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No data.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Builder</th>
            <th className="py-2 pr-3 text-right">Turns</th>
            <th className="py-2 pr-3 text-right">Success</th>
            <th className="py-2 pr-3 text-right">FT p50</th>
            <th className="py-2 pr-3 text-right">FT p95</th>
            <th className="py-2 pr-3 text-right">Total p95</th>
            <th className="py-2 pr-3 text-right">Tool ok</th>
            <th className="py-2 pr-3 text-right">Disc pass</th>
            <th className="py-2 pr-3 text-right">Timeouts</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((b) => {
            const badge = discoveryBadge(b);
            const totalTimeouts =
              b.timeoutFirstTokenCount + b.timeoutStreamInactivityCount;
            return (
              <tr key={b.builderKind} className="border-b border-border/50">
                <td className="py-2 pr-3 font-medium">
                  {BUILDER_KIND_LABELS[b.builderKind]}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {b.turnCount}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatPct(b.successRate)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatMs(b.firstTokenP50Ms)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatMs(b.firstTokenP95Ms)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {formatMs(b.totalP95Ms)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {b.toolCallAttemptCount > 0
                    ? formatPct(b.toolCallSuccessRate)
                    : '—'}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  <Badge
                    variant={
                      badge === 'green'
                        ? 'default'
                        : badge === 'red'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {DISCOVERY_BADGE_LABEL[badge]}
                  </Badge>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {totalTimeouts === 0 ? (
                    '0'
                  ) : (
                    <span className="text-destructive font-medium">
                      {totalTimeouts}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Per-tenant table ────────────────────────────────────────────────────

function PerTenantTable({
  rows,
}: {
  rows: Array<{
    organizationId: string | null;
    organizationName: string | null;
    turnCount: number;
    firstTokenP95Ms: number | null;
    totalP95Ms: number | null;
    successRate: number;
  }>;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No per-tenant data in this window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Tenant</th>
            <th className="py-2 pr-3 text-right">Turns</th>
            <th className="py-2 pr-3 text-right">Success</th>
            <th className="py-2 pr-3 text-right">FT p95</th>
            <th className="py-2 pr-3 text-right">Total p95</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.organizationId ?? 'former-tenant'}
              className="border-b border-border/50"
            >
              <td className="py-2 pr-3">
                {r.organizationName ?? (
                  <span className="text-muted-foreground italic">
                    {r.organizationId
                      ? r.organizationId.slice(0, 8)
                      : '(former tenant)'}
                  </span>
                )}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {r.turnCount}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatPct(r.successRate)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatMs(r.firstTokenP95Ms)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatMs(r.totalP95Ms)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Publish-gate table ──────────────────────────────────────────────────

function PublishGateTable({
  rows,
}: {
  rows: Array<{
    entityKind: string;
    approvalCount: number;
    timeInPendingMsP50: number | null;
    timeInPendingMsP95: number | null;
  }>;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No publish-gate approvals recorded in this window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-3">Entity kind</th>
            <th className="py-2 pr-3 text-right">Approvals</th>
            <th className="py-2 pr-3 text-right">Pending p50</th>
            <th className="py-2 pr-3 text-right">Pending p95</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.entityKind} className="border-b border-border/50">
              <td className="py-2 pr-3 font-medium">{r.entityKind}</td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {r.approvalCount}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatMs(r.timeInPendingMsP50)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {formatMs(r.timeInPendingMsP95)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Defensive — ensure the enum tuple is exhaustive at build time. If
// BUILDER_KIND_ENUM grows, this assertion fails and the dashboard
// must add the new label in BUILDER_KIND_LABELS.
const _exhaustivenessCheck: Record<BuilderKind, string> = BUILDER_KIND_LABELS;
void _exhaustivenessCheck;
void BUILDER_KIND_ENUM;
